import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { loadLogoForPDF, addLogoToPDF } from '../pdfLogoUtil';

const CaseloadDiagnosisPDF = ({ chartData, chartRef, user }) => {
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    setGenerating(true);

    try {
      const pdf = new jsPDF('l', 'mm', 'letter');
      const userName = user?.name || 'Unknown User';
      const userEmail = user?.email || '';
      const timestamp = new Date().toLocaleString();

      const logoDataUrl = await loadLogoForPDF();
      const addLogo = (x, yPos, w, h) => {
        addLogoToPDF(pdf, logoDataUrl, x, yPos, w, h);
      };

      const lPageWidth = pdf.internal.pageSize.getWidth();
      const lPageHeight = pdf.internal.pageSize.getHeight();
      const lMargin = 15;
      let y = lMargin;

      // Header with logo in top right
      const headerLogoH = 16;
      const headerLogoW = headerLogoH * 3;
      addLogo(lPageWidth - lMargin - headerLogoW, y, headerLogoW, headerLogoH);

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Caseload Breakdown by Diagnosis Analysis', lMargin, y + 6);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, lMargin, y + 12);
      y += headerLogoH + 4;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(lMargin, y, lPageWidth - lMargin, y);
      y += 5;

      // Chart image
      if (chartRef?.current) {
        try {
          const canvas = await html2canvas(chartRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
          });
          const chartImgData = canvas.toDataURL('image/png');

          const footerReserve = 25;
          const availableHeight = lPageHeight - y - footerReserve;
          const availableWidth = lPageWidth - lMargin * 2;

          const aspectRatio = canvas.height / canvas.width;
          let imgWidth = availableWidth;
          let imgHeight = imgWidth * aspectRatio;

          if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
            imgWidth = imgHeight / aspectRatio;
          }

          const imgX = lMargin + (availableWidth - imgWidth) / 2;
          pdf.addImage(chartImgData, 'PNG', imgX, y, imgWidth, imgHeight);
        } catch (e) {
          console.error('Chart capture failed:', e);
        }
      }

      // Page 2: Analytics
      if (chartData) {
        pdf.addPage('letter', 'portrait');
        const aPageWidth = pdf.internal.pageSize.getWidth();
        const aMargin = 20;
        y = aMargin;

        const { diagnosisData, uniqueClients, totalDiagnoses, top3, top3Percentage, diversityIndex, practiceType, isSpecialized, primaryDiagnosis } = chartData;

        // Logo in top right
        const aLogoH = 14;
        const aLogoW = aLogoH * 3;
        addLogo(aPageWidth - aMargin - aLogoW, y, aLogoW, aLogoH);

        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text('Caseload Diagnosis Analysis', aMargin, y + 6);
        y += 14;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, aMargin, y);
        y += 8;

        pdf.setDrawColor(180, 180, 180);
        pdf.line(aMargin, y, aPageWidth - aMargin, y);
        y += 10;

        // Practice Overview
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text('Practice Overview', aMargin, y);
        y += 8;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(40, 40, 40);

        const overviewLines = [
          `Total Clients: ${uniqueClients}`,
          `Diagnosis Types: ${totalDiagnoses}`,
          `Diversity Index: ${diversityIndex}/10`,
          `Primary Focus: ${primaryDiagnosis?.percentage.toFixed(1)}% - ${primaryDiagnosis?.diagnosis}`,
          `Practice Type: ${practiceType.charAt(0).toUpperCase() + practiceType.slice(1)} clinical practice`,
          `Clinical Identity: ${isSpecialized ? 'Specialist' : 'Generalist'} provider`,
          `Top 3 Diagnoses: ${top3Percentage.toFixed(1)}% of caseload`
        ];

        overviewLines.forEach(line => {
          pdf.text(`• ${line}`, aMargin + 4, y);
          y += 6;
        });
        y += 8;

        // Top Diagnoses
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text('Top Diagnostic Areas', aMargin, y);
        y += 8;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(40, 40, 40);

        diagnosisData.slice(0, 5).forEach(d => {
          pdf.text(`• ${d.diagnosis}: ${d.count} clients (${d.percentage.toFixed(1)}%)`, aMargin + 4, y);
          y += 6;
        });
      }

      // Page 3: Client Diagnostics
      if (chartData && chartData.diagnosisData && chartData.diagnosisData.length > 0) {
        const { diagnosisData } = chartData;
        pdf.addPage('letter', 'portrait');
        const cdPageWidth = pdf.internal.pageSize.getWidth();
        const cdPageHeight = pdf.internal.pageSize.getHeight();
        const cdMargin = 20;
        y = cdMargin;

        const cdLogoH = 14;
        const cdLogoW = cdLogoH * 3;
        addLogo(cdPageWidth - cdMargin - cdLogoW, y, cdLogoW, cdLogoH);

        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text('Client Diagnostics', cdMargin, y + 6);
        y += 14;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, cdMargin, y);
        y += 8;

        pdf.setDrawColor(180, 180, 180);
        pdf.line(cdMargin, y, cdPageWidth - cdMargin, y);
        y += 10;

        diagnosisData.forEach(d => {
          const clients = d.clientList || [];
          if (clients.length === 0) return;

          // New page if not enough room for heading + at least one line
          if (y > cdPageHeight - 45) {
            pdf.addPage('letter', 'portrait');
            y = cdMargin;
          }

          // Diagnosis heading
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 58, 138);
          pdf.text(`${d.diagnosis} (${d.clientCount} ${d.clientCount === 1 ? 'client' : 'clients'})`, cdMargin, y);
          y += 6;

          // Client IDs wrapped
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(60, 60, 60);
          const clientText = clients.join(', ');
          const wrappedLines = pdf.splitTextToSize(clientText, cdPageWidth - cdMargin * 2 - 6);
          wrappedLines.forEach(line => {
            if (y > cdPageHeight - 30) {
              pdf.addPage('letter', 'portrait');
              y = cdMargin;
            }
            pdf.text(line, cdMargin + 4, y);
            y += 5;
          });
          y += 8;
        });
      }

      pdf.save(`Caseload_Diagnosis_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const hasData = chartData && chartData.diagnosisData && chartData.diagnosisData.length > 0;

  return (
    <button
      onClick={generatePDF}
      disabled={generating || !hasData}
      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
        generating
          ? 'bg-gray-300 text-gray-500 cursor-wait'
          : !hasData
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
      }`}
    >
      {generating ? 'Generating PDF...' : 'Download PDF'}
    </button>
  );
};

export default CaseloadDiagnosisPDF;
